import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './pharmacy-refund-bill-item.reducer';

export const PharmacyRefundBillItem = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const pharmacyRefundBillItemList = useAppSelector(state => state.pharmacyRefundBillItem.entities);
  const loading = useAppSelector(state => state.pharmacyRefundBillItem.loading);
  const totalItems = useAppSelector(state => state.pharmacyRefundBillItem.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="pharmacy-refund-bill-item-heading" data-cy="PharmacyRefundBillItemHeading">
        <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.home.title">Pharmacy Refund Bill Items</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/pharmacy-refund-bill-item/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.home.createLabel">
              Create new Pharmacy Refund Bill Item
            </Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.pharmacyRefundBillItem.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {pharmacyRefundBillItemList && pharmacyRefundBillItemList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('chargedMrp')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.chargedMrp">Charged Mrp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('chargedMrp')} />
                </th>
                <th className="hand" onClick={sort('refundPaid')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.refundPaid">Refund Paid</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refundPaid')} />
                </th>
                <th className="hand" onClick={sort('refundQty')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.refundQty">Refund Qty</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refundQty')} />
                </th>
                <th className="hand" onClick={sort('soldQty')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.soldQty">Sold Qty</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('soldQty')} />
                </th>
                <th className="hand" onClick={sort('manufacDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.manufacDate">Manufac Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('manufacDate')} />
                </th>
                <th className="hand" onClick={sort('expDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.expDate">Exp Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('expDate')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.pharmacyStock">Pharmacy Stock</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.saleBillItem">Sale Bill Item</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.refundBill">Refund Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.saleBill">Sale Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.returnBill">Return Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.returnItem">Return Item</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pharmacyRefundBillItemList.map((pharmacyRefundBillItem, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/pharmacy-refund-bill-item/${pharmacyRefundBillItem.id}`} color="link" size="sm">
                      {pharmacyRefundBillItem.id}
                    </Button>
                  </td>
                  <td>{pharmacyRefundBillItem.chargedMrp}</td>
                  <td>{pharmacyRefundBillItem.refundPaid}</td>
                  <td>{pharmacyRefundBillItem.refundQty}</td>
                  <td>{pharmacyRefundBillItem.soldQty}</td>
                  <td>{pharmacyRefundBillItem.manufacDate}</td>
                  <td>{pharmacyRefundBillItem.expDate}</td>
                  <td>{pharmacyRefundBillItem.active ? 'true' : 'false'}</td>
                  <td>{pharmacyRefundBillItem.cursor}</td>
                  <td>{pharmacyRefundBillItem.timestampentry}</td>
                  <td>{pharmacyRefundBillItem.microSec}</td>
                  <td>{pharmacyRefundBillItem.lastupdated}</td>
                  <td>
                    {pharmacyRefundBillItem.pharmacyStock ? (
                      <Link to={`/pharmacy-stock/${pharmacyRefundBillItem.pharmacyStock.id}`}>
                        {pharmacyRefundBillItem.pharmacyStock.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBillItem.saleBillItem ? (
                      <Link to={`/pharmacy-bill-item/${pharmacyRefundBillItem.saleBillItem.id}`}>
                        {pharmacyRefundBillItem.saleBillItem.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBillItem.refundBill ? (
                      <Link to={`/pharmacy-refund-bill/${pharmacyRefundBillItem.refundBill.id}`}>
                        {pharmacyRefundBillItem.refundBill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBillItem.saleBill ? (
                      <Link to={`/pharmacy-bill/${pharmacyRefundBillItem.saleBill.id}`}>{pharmacyRefundBillItem.saleBill.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBillItem.returnBill ? (
                      <Link to={`/pharmacy-return-bill/${pharmacyRefundBillItem.returnBill.id}`}>
                        {pharmacyRefundBillItem.returnBill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyRefundBillItem.returnItem ? (
                      <Link to={`/pharmacy-return-bill-item/${pharmacyRefundBillItem.returnItem.id}`}>
                        {pharmacyRefundBillItem.returnItem.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/pharmacy-refund-bill-item/${pharmacyRefundBillItem.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/pharmacy-refund-bill-item/${pharmacyRefundBillItem.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/pharmacy-refund-bill-item/${pharmacyRefundBillItem.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.pharmacyRefundBillItem.home.notFound">No Pharmacy Refund Bill Items found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={pharmacyRefundBillItemList && pharmacyRefundBillItemList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PharmacyRefundBillItem;
